import React from "react";
import { Alert, Input, Tag } from "antd";
import useLang from "../../../hooks/useLang";
import { ParsedOrderIds } from "../parseOrderIds";
import { MAX_ORDER_IDS } from "../../../utils/bulkOperationsConfig";

// A 5000-id paste would otherwise render 5000 tags.
const MAX_REJECTED_TAGS = 50;

interface OrderIdsInputProps {
	value: string;
	onChange: (value: string) => void;
	parsed: ParsedOrderIds;
	tooManyIds: boolean;
}

const OrderIdsInput = ({
	value,
	onChange,
	parsed,
	tooManyIds,
}: OrderIdsInputProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;

	return (
		<div>
			<div className="font-medium mb-2">
				{common?.orderIdsLabel ?? "Order IDs"}
			</div>
			<Input.TextArea
				rows={10}
				className="font-mono"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={
					common?.orderIdsPlaceholder ??
					"Paste order ids — e.g. 4003935 or DF32-123123, separated by new lines, commas or spaces"
				}
			/>
			<div className="flex flex-wrap items-center gap-2 mt-2">
				<Tag color="green">
					{parsed.valid.length} {common?.validCount ?? "valid ids"}
				</Tag>
				{parsed.duplicateCount > 0 && (
					<Tag>
						{parsed.duplicateCount}{" "}
						{common?.duplicatesRemoved ?? "duplicates removed"}
					</Tag>
				)}
			</div>
			{parsed.rejected.length > 0 && (
				<Alert
					className="mt-2"
					type="warning"
					showIcon
					message={
						common?.rejectedTokens ??
						"Unrecognized entries (will not be sent):"
					}
					description={
						<div className="flex flex-wrap gap-1">
							{parsed.rejected
								.slice(0, MAX_REJECTED_TAGS)
								.map((t) => (
									<Tag color="red" key={t}>
										{t}
									</Tag>
								))}
							{parsed.rejected.length > MAX_REJECTED_TAGS && (
								<Tag>
									+
									{parsed.rejected.length - MAX_REJECTED_TAGS}
								</Tag>
							)}
						</div>
					}
				/>
			)}
			{tooManyIds && (
				<Alert
					className="mt-2"
					type="error"
					showIcon
					message={
						common?.tooManyIds ??
						`Maximum ${MAX_ORDER_IDS} order ids per request`
					}
				/>
			)}
		</div>
	);
};

export default OrderIdsInput;
